import { BillingProduct, InviteCreateParams, ProgramCreateParams } from '@citruscamps/citrus-client'
import React, { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { AuthForm } from '../../../../components/AuthForm/AuthForm'
import { LoadingForm } from '../../../../components/LoadingForm/LoadingForm'
import { ROUTES } from '../../../../constants/routes'
import { useAuth } from '../../../../hooks/useAuth'
import { useRouter } from '../../../../hooks/useRouter'
import translations from '../../../../translations/en.json'
import { Address } from '../../../../utils/address'
import { toSingleQueryValue } from '../../../../utils/query-params'
import { InviteStep } from '../Signup/components/InviteStep'
import { MoreDetailStep } from './components/MoreDetailStep'
import { PlanStep } from './components/PlanStep'
import { ProgramStep } from './components/ProgramStep'
import { SubscribeStep } from './components/SubscribeStep'
import { useFetchBillingPlans } from './hooks/useFetchPlans'
import { useSetupProgram } from './hooks/useSetupProgram'
import { useSetupProgramWizard } from './hooks/useSetupProgramWizard'

type UtmSource = 'google' | 'facebook' | 'social' | 'partner' | 'direct' | 'other'
type UtmMedium = 'organic' | 'cpc' | 'referral' | 'email' | 'none'

export const SetupProgram = (): React.ReactElement => {
  const { user, program, isLoading, setProgramId } = useAuth()
  const { location, query, ...router } = useRouter()
  const { goto, utm_source, utm_medium, utm_campaign, utm_term, utm_content } =
    toSingleQueryValue(query)
  const [error, setError] = useState<React.ReactElement | undefined>()
  const { step, steps, nextStep, previousStep } = useSetupProgramWizard()
  const [createParams, setCreateParams] = useState<ProgramCreateParams>({
    name: '',
    address: undefined!,
    types: [],
    tags: [],
    traffic_source: {
      utm_source: (utm_source as UtmSource) || 'direct',
      utm_medium: (utm_medium as UtmMedium) || 'none',
      utm_campaign: utm_campaign || 'none',
      utm_term,
      utm_content,
    },
    items: query.product_id
      ? typeof query.product_id === 'string'
        ? [query.product_id]
        : query.product_id
      : [],
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  })
  const { handleTrackAction: sendEvent, handleCreate, handleCreateInvite } = useSetupProgram()
  const { data: products, isLoading: isLoadingProducts } = useFetchBillingPlans({
    fetchProps: { filter: { currency: createParams.default_currency || 'cad' } },
  })
  const isMatchingPlan = (p: BillingProduct): boolean =>
    p.type === query.type &&
    p.interval === query.interval &&
    (!query.currency || p.amount.currency_code === query.currency)
  const product: BillingProduct | undefined = products
    .filter((p) => p.active)
    .find((p) => createParams.items?.some((i) => i === p.id) || isMatchingPlan(p))

  const handlePrevious = (inputs?: ProgramCreateParams): void => {
    if (inputs) setCreateParams({ ...inputs })
    previousStep()
    setError(undefined)
  }

  const handleNext = (): void => {
    nextStep()
    setError(undefined)
  }

  if (isLoading) {
    return (
      <AuthForm
        useProgress={true}
        totalSteps={steps.length}
        currentStep={steps.findIndex((s) => s === step) + 1}
      >
        <LoadingForm />
      </AuthForm>
    )
  }

  if (!user) {
    return <Navigate to={{ pathname: ROUTES.SIGNUP, search: location.search }} replace />
  }

  return (
    <AuthForm
      useProgress={true}
      totalSteps={steps.length}
      currentStep={steps.findIndex((s) => s === step) + 1}
      error={error}
      cardFooter={
        program ? (
          <span className="d-block w-100 text-center">
            <Link to={ROUTES.DASHBOARD} className="ms-2">
              Already have a program? Go to dashboard
            </Link>
          </span>
        ) : undefined
      }
    >
      {step === 'program' && (
        <ProgramStep
          initialValues={createParams}
          onSubmit={async (inputs: ProgramCreateParams) => {
            setCreateParams({ ...inputs })
            sendEvent({
              step: 'program_name',
            })
            handleNext()
          }}
        />
      )}
      {step === 'more_detail' && (
        <MoreDetailStep
          initialValues={createParams}
          onSubmit={async (inputs: ProgramCreateParams): Promise<void> => {
            setCreateParams({ ...inputs })
            sendEvent({
              step: 'program_details',
            })
            handleNext()
          }}
          onPrevious={handlePrevious}
        />
      )}
      {step === 'plan' && isLoadingProducts && <LoadingForm />}
      {step === 'plan' && !isLoadingProducts && (
        <PlanStep
          initialValues={{
            product_id: product?.id,
            interval: product?.interval || 'year',
          }}
          products={products}
          language={createParams.language}
          address={Address.fromGeocode(createParams?.address)}
          onPrevious={handlePrevious}
          onSubmit={async (_product: BillingProduct): Promise<void> => {
            setCreateParams((prev) => ({ ...prev, items: [_product.id] }))
            sendEvent({
              step: 'program_plan',
              value: _product.amount.major_value || 0,
              currency: _product.amount.currency_code,
              items: [_product],
            })
            handleNext()
          }}
        />
      )}
      {step === 'subscribe' && (
        <SubscribeStep
          initialValues={createParams}
          product={product}
          onPrevious={handlePrevious}
          onSubmit={async (inputs: ProgramCreateParams): Promise<void> => {
            try {
              const program = await handleCreate(inputs)
              setProgramId(program.id)
              sendEvent({
                step: 'program_complete',
              })
              handleNext()
            } catch (e: any) {
              setError(e.message || (translations as any)['error.default_toast_message'])
            }
          }}
        />
      )}
      {step === 'invite' && (
        <InviteStep
          initialValues={{ program_id: program?.id, timezone: program?.timezone }}
          onSkip={async (): Promise<any> => {
            if (program) {
              sendEvent({
                step: 'program_invite',
              })
              setProgramId(program.id)
              await router.push(goto || ROUTES.DASHBOARD)
            } else {
              setError((translations as any)['error.default_toast_message'])
            }
          }}
          onSubmit={async (inputs: InviteCreateParams): Promise<any> => {
            if (program) {
              if (inputs.email && inputs.first_name) {
                try {
                  await handleCreateInvite({
                    ...inputs,
                    program_id: program.id,
                  })
                } catch (e: any) {
                  setError(e.message || (translations as any)['error.default_toast_message'])
                  throw e
                }
              }
              await router.push(goto || ROUTES.DASHBOARD)
              sendEvent({
                step: 'program_invite',
              })
            } else {
              setError((translations as any)['error.default_toast_message'])
            }
          }}
        />
      )}
    </AuthForm>
  )
}
